import { createListenerMiddleware, isAnyOf } from "@reduxjs/toolkit";
import dayjs from "dayjs";
import lodash from "lodash";
import { createRequest } from "../../utility";
import {
  fetchMonitoraggio,
  fetchMonitoraggioSuccess,
  fetchMonitoraggioFailed,
  fetchUpdateMonitoraggio,
  fetchUpdateMonitoraggioFailed,
  fetchUpdateMonitoraggioSuccess,
  azzeraMonitoraggio,
  ottieniInformazioniEncounter,
  checkEncounterChanges,
  setEncounterNotification,
  resetEncounterNotification,
  CODICE_EVENTO,
  EVENT_COMPLETED,
  setFailedEncounterCalls,
} from "./index";
import { TENANT_ID } from "../configuration";
import {
  CODICE_ACCESSO_EXPIRED,
  CODICE_ACCESSO_EXPIRED_DESCRIPTION,
  PS_FUORI_SERVIZIO,
  PS_FUORI_SERVIZIO_DESCRIPTION,
  SAGAS_SERVER_ERROR_DESCRIPTION,
  SAGAS_SERVER_ERROR_TITLE,
  TOO_MANY_REQUEST_ERROR_TITLE,
  TOO_MANY_REQUEST_ERROR_DESCRIPTION,
} from "../language";
import { setNotification, notificationCodes, resetNotification } from "../notification";
import { CONFIG_API, ENCOUNTER_API } from "../../apiUrl";
import { NOME_OSPEDALE_BREVE_JSON } from "../prontoSoccorso";

const fetchMonitoraggioWorker = (action, listenerApi) => {
  const { idPaziente } = action.payload;
  createRequest(ENCOUNTER_API.method, ENCOUNTER_API.baseUrl, ENCOUNTER_API.apiUrl, null, null, { [TENANT_ID]: idPaziente })
    .then((response) => {
      if (response.status === 200) {
        listenerApi.dispatch(fetchMonitoraggioSuccess({ [CODICE_EVENTO]: idPaziente, ...ottieniInformazioniEncounter(response.data) }));
      } else throw response.message;
    })
    .catch((e) => {
      if ([400, 404].includes(e.response.status)) {
        listenerApi.dispatch(setNotification({ tipo: notificationCodes.ERROR, message: CODICE_ACCESSO_EXPIRED, description: CODICE_ACCESSO_EXPIRED_DESCRIPTION })); // prettier-ignore
        window.ReactNativeWebView?.postMessage(JSON.stringify({ notFound: idPaziente }));
      } else if (e.response.status === 451) {
        const elencoPs = listenerApi.getState().prontoSoccorso.elencoProntoSoccorso.data;
        console.log("RESPONSE", e.response);
        listenerApi.dispatch(setNotification({ tipo: notificationCodes.ERROR, message: PS_FUORI_SERVIZIO, description: PS_FUORI_SERVIZIO_DESCRIPTION, parametri: { nomePs: elencoPs.find((ps) => ps.id === e.response.data.idPs)?.[NOME_OSPEDALE_BREVE_JSON] } })); // prettier-ignore
        window.ReactNativeWebView?.postMessage(JSON.stringify({ notFound: idPaziente }));
      } else if (e.response.status === 429 || e.response.status.toString().startsWith("5")) {
        const maxFailedCalls = listenerApi.getState().configuration.data.maxFailedCalls;
        const failedCalls = listenerApi.getState().tracking.monitoraggio.failedCalls + 1;
        if (failedCalls === maxFailedCalls)
          listenerApi.dispatch(
            setNotification({
              tipo: notificationCodes.ERROR,
              message: e.response.statuse === 429 ? TOO_MANY_REQUEST_ERROR_TITLE : SAGAS_SERVER_ERROR_TITLE,
              description: e.response.statuse === 429 ? TOO_MANY_REQUEST_ERROR_DESCRIPTION : SAGAS_SERVER_ERROR_DESCRIPTION,
            })
          );
        else listenerApi.dispatch(setFailedEncounterCalls(failedCalls));
      }
      listenerApi.dispatch(fetchMonitoraggioFailed());
    });
};

const fetchUpdateMonitoraggioWorker = (listenerApi) => {
  createRequest(ENCOUNTER_API.method, ENCOUNTER_API.baseUrl, ENCOUNTER_API.apiUrl)
    .then((response) => {
      if (response.status === 200) {
        let prevState = listenerApi.getState().tracking.monitoraggio.data;
        let newState = ottieniInformazioniEncounter(response.data);
        if (!lodash.isEmpty(prevState)) {
          const check = checkEncounterChanges(prevState, newState);
          if (check.isChanged) {
            listenerApi.dispatch(
              setEncounterNotification({
                timestamp: dayjs(),
                changes: check.changes,
                prestazioniChanges: check.prestazioniChanges,
              })
            );
          }
        }
        listenerApi.dispatch(fetchUpdateMonitoraggioSuccess(ottieniInformazioniEncounter(response.data)));
      } else throw response.message;
    })
    .catch((e) => {
      if ([400, 451].includes(e.response.status)) window.location.href = window.location.origin;
      else if (e.response.status === 404) {
        window.dispatchEvent(new CustomEvent(EVENT_COMPLETED));
        window.ReactNativeWebView?.postMessage(JSON.stringify({ notFound: listenerApi.getState().tracking.monitoraggio.data[CODICE_EVENTO] }));
      } else if (e.response.status === 429 || e.response.status.toString().startsWith("5")) {
        const maxFailedCalls = listenerApi.getState().configuration.data.maxFailedCalls;
        const failedCalls = listenerApi.getState().tracking.monitoraggio.failedCalls + 1;
        if (failedCalls === maxFailedCalls) {
          listenerApi.dispatch(
            setNotification({
              tipo: notificationCodes.ERROR,
              message: e.response.statuse === 429 ? TOO_MANY_REQUEST_ERROR_TITLE : SAGAS_SERVER_ERROR_TITLE,
              description: e.response.statuse === 429 ? TOO_MANY_REQUEST_ERROR_DESCRIPTION : SAGAS_SERVER_ERROR_DESCRIPTION,
            })
          );
          createRequest(CONFIG_API.method, CONFIG_API.baseUrl, CONFIG_API.apiUrl).then((res) => {
            if (res.data.notAvailable) window.location.href = window.origin;
          });
        } else listenerApi.dispatch(setFailedEncounterCalls(failedCalls));
      }
      listenerApi.dispatch(fetchUpdateMonitoraggioFailed());
    });
};

export const trackingMiddleware = createListenerMiddleware();

trackingMiddleware.startListening({
  actionCreator: fetchMonitoraggio,
  effect: async (action, listenerApi) => fetchMonitoraggioWorker(action, listenerApi),
});

trackingMiddleware.startListening({
  actionCreator: fetchUpdateMonitoraggio,
  effect: async (action, listenerApi) => fetchUpdateMonitoraggioWorker(listenerApi),
});

trackingMiddleware.startListening({
  actionCreator: azzeraMonitoraggio,
  effect: async (action, listenerApi) => listenerApi.dispatch(resetEncounterNotification()),
});

trackingMiddleware.startListening({
  matcher: isAnyOf(fetchMonitoraggioSuccess, fetchUpdateMonitoraggioSuccess),
  effect: async (action, listenerApi) => {
    if (listenerApi.getState().notification) listenerApi.dispatch(resetNotification());
  },
});
